<template>
  <div class="markdown-editor prose max-w-none">
    <div v-if="contentOnly" class="content-only">
      <div 
        v-if="localContent && !shouldRenderLatexPreview"
        ref="previewRef"
        class="prose prose-neutral dark:prose-invert max-w-none preview-content"
        v-html="renderedContent" 
      />
      <div
        v-else-if="localContent && shouldRenderLatexPreview"
        ref="latexPreviewRef"
        class="latex-content mt-4"
        v-html="renderedLaTeXContent" 
      />
      <div v-else class="empty-preview">
        Nothing to preview
      </div>
    </div>
    <div 
      v-else
      class="markdown-container"
      :class="{ 'preview-mode': activeTab === 'preview' }"
    >
      <div class="tab-container">
        <button 
          @click="activeTab = 'write'"
          :class="[
            'tab-button',
            activeTab === 'write' 
              ? 'tab-button--active' 
              : 'tab-button--inactive'
          ]"
        >
          Write
        </button>
        <button 
          @click="activeTab = 'preview'"
          :class="[
            'tab-button',
            activeTab === 'preview' 
              ? 'tab-button--active' 
              : 'tab-button--inactive'
          ]"
        >
          Preview
        </button>
        <div class="flex-grow"></div>
        <button 
          v-if="activeTab === 'write' && enableImageUpload"
          @click="handleImageClick"
          class="image-btn"
          :disabled="disabled"
          title="Add Image (or paste from clipboard)"
        >
          <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </button>
      </div>

      <div v-if="activeTab === 'write'" class="content-container">
        <textarea
          v-model="localContent"
          class="markdown-input w-full h-full"
          :placeholder="placeholder"
          @input="handleInput"
          @paste="$emit('paste', $event)"
          :disabled="disabled"
          ref="textareaRef"
          :rows="rowsLength > 0 ? rowsLength : undefined"
          :data-testid="dataTestId"
          :x
        ></textarea>
      </div>

      <div v-if="activeTab === 'preview'" class="content-container preview-tab-container">
        <div 
          v-if="localContent && !shouldRenderLatexPreview"
          ref="previewRef"
          class="prose prose-neutral dark:prose-invert max-w-none preview-content"
          v-html="renderedContent" 
        />
        <div
          v-else-if="localContent && shouldRenderLatexPreview"
          ref="latexPreviewRef"
          class="latex-content mt-4"
          v-html="renderedLaTeXContent" 
        />
        <div v-else class="empty-preview">
          Nothing to preview
        </div>
      </div>
    </div>
  </div>
</template>
  
<script setup>
import { defineProps, defineEmits, computed, ref, watch, onMounted, nextTick } from 'vue';
import markdownit from "markdown-it";
import texmath from "markdown-it-texmath";
import katex from "katex";
import { parse, HtmlGenerator } from 'latex.js';
import DOMPurify from 'dompurify';

const props = defineProps({
  modelValue: {
    type: String,
    default: ""
  },
  placeholder: {
    type: String,
    default: "Write your content here..."
  },
  isEditing: {
    type: Boolean,
    default: false
  },
  contentOnly: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  maxHeightBody: {
    type: String,
    default: '100%'
  },
  dataTestId: {
    type: String,
    default: ''
  },
  enableImageUpload: {
    type: Boolean,
    default: true
  },
  rowsLength: {
    type: Number,
    default: 0
  }
});

const emit = defineEmits(['update:modelValue', 'paste']);

const activeTab = ref(props.isEditing ? 'preview' : 'write');
const localContent = ref(props.modelValue);

const markdown = markdownit({
  linkify: true,
  breaks: true,
  html: false
});

// Configure texmath with katex engine
markdown.use(texmath, {
  engine: katex,
  delimiters: 'dollars', // supports $...$ and $$...$$
  katexOptions: { macros: { "\\RR": "\\mathbb{R}" } }
});

const defaultRender = markdown.renderer.rules.link_open || function(tokens, idx, options, env, self) {
  return self.renderToken(tokens, idx, options);
};

markdown.renderer.rules.link_open = function (tokens, idx, options, env, self) {
  const aIndex = tokens[idx].attrIndex('target');
  const relIndex = tokens[idx].attrIndex('rel');
  
  if (aIndex < 0) {
    tokens[idx].attrPush(['target', '_blank']);
  } else {
    tokens[idx].attrs[aIndex][1] = '_blank';
  }
  
  if (relIndex < 0) {
    tokens[idx].attrPush(['rel', 'noopener noreferrer']);
  } else {
    tokens[idx].attrs[relIndex][1] = 'noopener noreferrer';
  }

  return defaultRender(tokens, idx, options, env, self);
};

watch(() => props.modelValue, (newValue) => {
  if (newValue !== localContent.value) {
    localContent.value = newValue;
  }
});

const textareaRef = ref(null);
const previewRef = ref(null);
const latexPreviewRef = ref(null);
const contentHeight = ref('100%');

// Helper function to detect full LaTeX documents
const isLatexDocument = (content) => {
  if (!content) return false;
  return content.includes('\\begin{document}') || content.includes('\\documentclass');
};

// Helper function to detect if content contains LaTeX
const isLaTeXContent = (content) => {
  if (!content) return false;
  if (isLatexDocument(content)) return true;
  
  const latexPatterns = [
    /\\[a-zA-Z]+\{/,           
    /\\begin\{[^}]+\}/,         
    /\\end\{[^}]+\}/,           
    /\\section\{/,              
    /\\subsection\{/,           
    /\\textbf\{/,               
    /\\textit\{/,               
    /\\[a-zA-Z]+\(/,            
  ];
  return latexPatterns.some(pattern => pattern.test(content));
};

// Helper function to prepare LaTeX content for parsing
const prepareLaTeXContent = (content) => {
  if (!content) return '';
  if (isLatexDocument(content)) return content;
  return `\\documentclass{article}\n\\begin{document}\n${content}\n\\end{document}`;
};

// Render LaTeX only when the content is a full LaTeX document.
const shouldRenderLatexPreview = computed(() => {
  return isLatexDocument(localContent.value) && isLaTeXContent(localContent.value);
});

const renderedLaTeXContent = computed(() => {
  if (!localContent.value || !shouldRenderLatexPreview.value) return '';
  try {
    const latexContent = prepareLaTeXContent(localContent.value);
    const generator = new HtmlGenerator({ hyphenate: false });
    const doc = parse(latexContent, { generator: generator }).htmlDocument();
    return sanitizeHtml(doc.body.innerHTML);
  } catch (error) {
    console.warn('LaTeX rendering skipped:', error.message);
    return '';
  }
});

const updateContentHeight = () => {
  if (textareaRef.value) {
    contentHeight.value = '100%';
    textareaRef.value.style.height = '100%';
  }
};

watch(localContent, () => {
  nextTick(() => { updateContentHeight(); });
}, { immediate: true });

watch(shouldRenderLatexPreview, (newValue) => {
  if (newValue) { nextTick(() => { updateContentHeight(); }); }
}, { immediate: true });

watch(activeTab, () => {
  nextTick(() => { updateContentHeight(); });
});

onMounted(() => {
  nextTick(() => { updateContentHeight(); });
});

const handleInput = () => {
  emit('update:modelValue', localContent.value);
  nextTick(() => { updateContentHeight(); });
};
// Block javascript:/vbscript: (eval-like), expression(), -moz-binding, behavior: url()
const isDangerousStyleValue = (value) => {
  const v = value.toLowerCase();
  const noSpaces = v.replace(/\s/g, '');
  return (
    v.includes('expression(') ||
    noSpaces.includes('javascript:') ||
    noSpaces.includes('vbscript:') ||
    v.includes('-moz-binding') ||
    (v.includes('behavior') && v.includes('url('))
  );
};

const sanitizeStyleValue = (css) => {
  if (!css || typeof css !== 'string') return '';
  return css
    .split(';')
    .filter((decl) => {
      const idx = decl.indexOf(':');
      if (idx === -1) return false;
      return !isDangerousStyleValue(decl.slice(idx + 1));
    })
    .join(';')
    .trim();
};

let styleSanitizerHookRegistered = false;
if (!styleSanitizerHookRegistered) {
  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    if (data.attrName === 'style') {
      data.attrValue = sanitizeStyleValue(data.attrValue);
    }
  });
  styleSanitizerHookRegistered = true;
}

const sanitizeHtml = (unsafeHtml) => DOMPurify.sanitize(unsafeHtml ?? '', {
  USE_PROFILES: { html: true, svg: true, mathMl: true },
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'link', 'meta', 'style'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus'],
  ADD_ATTR: ['target', 'rel', 'href', 'class', 'style'],
  ALLOW_DATA_ATTR: false
});

const renderedContent = computed(() => {
  return sanitizeHtml(markdown.render(localContent.value || ''));
});

const handleImageClick = () => {
  const imageInput = document.createElement('input');
  imageInput.type = 'file';
  imageInput.accept = 'image/*';
  imageInput.click();
  
  imageInput.onchange = (event) => {
    const file = event.target.files[0];
    if (file) {
      emit('paste', event);
    }
  };
};
</script>

<style lang="postcss">
@import "katex/dist/katex.min.css";
@import "markdown-it-texmath/css/texmath.css";

.markdown-editor {
  @apply w-full h-full max-w-none overflow-x-hidden;
}

.prose :where(code):not(:where([class~="not-prose"], [class~="not-prose"] *)) {
  @apply bg-white/10 text-white px-1.5 py-0.5 rounded text-sm;
  border: 1px solid rgba(255, 255, 255, 0.1);
  font-weight: normal !important;
}

.prose :where(code):not(:where([class~="not-prose"], [class~="not-prose"] *))::before,
.prose :where(code):not(:where([class~="not-prose"], [class~="not-prose"] *))::after {
  content: '' !important;
}

.prose :where(pre):not(:where([class~="not-prose"], [class~="not-prose"] *)) {
  @apply bg-echoes-background-gray rounded-lg p-4 overflow-x-auto;
  border: 1px solid theme('colors.echoes.border.light');
}

.prose :where(pre code):not(:where([class~="not-prose"], [class~="not-prose"] *)) {
  @apply bg-transparent text-gray-200 text-sm;
  padding: 0 !important;
  border: none !important;
  font-weight: normal !important;
}

.prose :where(a):not(:where([class~="not-prose"], [class~="not-prose"] *)) {
  @apply text-echoes-primary underline cursor-pointer;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
  transition: all 0.2s ease-in-out;
}

.prose :where(a):not(:where([class~="not-prose"], [class~="not-prose"] *)):hover {
  @apply text-echoes-primary/80;
  text-decoration-thickness: 2px;
}

.prose :where(a):not(:where([class~="not-prose"], [class~="not-prose"] *)):visited {
  @apply text-purple-400;
}

.markdown-container {
  @apply w-full h-full rounded-2xl overflow-hidden
    bg-echoes-background-black
    border border-echoes-border-gray
    transition-all duration-200 ease-in-out
    hover:bg-echoes-background-black/50
    hover:border-echoes-border-gray
    flex flex-col;

  &.preview-mode {
    @apply focus-within:ring-0 focus-within:border-echoes-border-gray;
    
    &:hover {
      @apply focus-within:border-echoes-border-gray;
    }
  }
}

.tab-container {
  @apply flex items-center bg-echoes-background-gray;
}

.tab-button {
  @apply px-4 py-2 text-sm transition-colors;
}

.tab-button--active {
  @apply text-white bg-echoes-background-black;
}

.tab-button--inactive {
  @apply text-white/80 bg-echoes-background-gray hover:bg-echoes-background-black/50 hover:text-echoes-primary;
}

.content-container {
  @apply p-3 text-xs bg-echoes-background-black overflow-x-hidden transition-all duration-200 flex-1 flex flex-col;
  height: v-bind(contentHeight);
}

.preview-tab-container {
  @apply overflow-auto;
  height: auto !important;
  min-height: 10rem;
}

.markdown-input {
  @apply w-full h-full text-sm
    bg-transparent
    text-white
    border-none outline-none
    placeholder-white/50
    transition-all duration-200 ease-in-out
    resize-none 
    overflow-auto;
  height: v-bind(contentHeight);

  &::placeholder {
    @apply transition-all duration-200;
  }

  &:hover::placeholder {
    @apply text-white/80;
  }

  &:focus::placeholder {
    @apply opacity-0 translate-x-1;
  }
}

.preview-content {
  @apply overflow-visible;
  height: auto;

  a {
    pointer-events: auto;
    cursor: pointer;
  }
}

.empty-preview {
  @apply flex items-center justify-center min-h-[10rem]
    text-white/80 text-sm
    select-none;
}

.content-only {
  @apply w-full overflow-x-hidden;

  .preview-content {
    @apply text-gray-200;
  }
  
  a, button, input, select, textarea {
    pointer-events: auto;
    cursor: pointer;
  }
}

.latex-content {
  @apply text-gray-200;
  height: auto;
}

.latex-content :deep(img) {
  max-width: 100%;
  height: auto;
}

.latex-content :deep(svg) {
  max-width: 100%;
  height: auto;
}

.image-btn {
  @apply p-2 mr-4 rounded-[6px] text-white/80 hover:text-echoes-primary hover:bg-echoes-background-black/50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed;
}

.flex-grow {
  @apply flex-1;
}

.prose :where(table):not(:where([class~="not-prose"] *)) {
  @apply w-full;
  display: block;
  overflow-x: auto;

}

.prose :where(thead, tbody):not(:where([class~="not-prose"] *)) {
  display: table-header-group;
  width: 100%;
}

.prose :where(tbody):not(:where([class~="not-prose"] *)) {
  display: table-row-group;
}

.prose :where(tr):not(:where([class~="not-prose"] *)) {
  display: table-row;
  width: 100%;
}

.prose :where(th, td):not(:where([class~="not-prose"] *)) {
  @apply px-4 py-3;
  display: table-cell;
  border-right: 1px solid theme('colors.echoes.border.light');
  min-width: 150px;
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: top;
  position: relative;
}

.prose :where(th):not(:where([class~="not-prose"] *)) {
  @apply font-semibold text-white;
  background: theme('colors.echoes.background.gray');
  border-bottom: 1px solid theme('colors.echoes.border.light');
  position: sticky;
  top: 0;
  z-index: 10;
}

.prose :where(td):not(:where([class~="not-prose"] *)) {
  color: theme('colors.gray.200');
  border-bottom: 1px solid theme('colors.echoes.border.light');
  background: theme('colors.echoes.background.black');
}

.prose :where(th:last-child, td:last-child):not(:where([class~="not-prose"] *)) {
  border-right: none;
}

.prose :where(tbody tr:last-child td):not(:where([class~="not-prose"] *)) {
  border-bottom: none;
}

.prose :where(th *, td *):not(:where([class~="not-prose"] *)) {
  max-width: 100%;
  word-break: break-word;
  overflow-wrap: break-word;
}

.prose :where(th p, td p):not(:where([class~="not-prose"] *)) {
  margin: 0;
  padding: 0;
  line-height: 1.4;
}

.prose :where(th code, td code):not(:where([class~="not-prose"] *)) {
  @apply text-xs px-1 py-0.5 rounded;
  background: rgba(255, 255, 255, 0.1);
  color: theme('colors.echoes.primary');
  white-space: nowrap;
}
</style>